import {
  Bold,
  Code,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo2,
  RemoveFormatting,
  SquareCode,
  Underline,
  Undo2,
} from 'lucide-react';
import { Fragment, useEffect, useImperativeHandle, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent, type ReactNode, type Ref } from 'react';
import { Button, cn, Field, Input, Modal, Spinner } from './ui';

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

/** A run of text with the same formatting. */
export interface RichInline {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  href?: string;
}

export type RichBlock =
  | { type: 'paragraph'; content: RichInline[] }
  | { type: 'heading'; level: 2 | 3; content: RichInline[] }
  | { type: 'quote'; content: RichInline[] }
  | { type: 'list'; ordered: boolean; items: RichInline[][] }
  | { type: 'code'; text: string }
  | { type: 'image'; imageId: string; alt: string }
  | { type: 'divider' };

/** Formatted text as data rather than HTML, so it can be checked on the server and shown without injecting markup. */
export interface RichDocument {
  version: 1;
  blocks: RichBlock[];
}

export const EMPTY_DOCUMENT: RichDocument = { version: 1, blocks: [] };

/** Links go to web pages, email addresses, or phone numbers - never scripts. */
export const SAFE_HREF = /^(https?:\/\/|mailto:|tel:)/i;

const inlineText = (content: RichInline[]) => content.map((part) => part.text).join('');

export const isDocumentEmpty = (doc: RichDocument) =>
  doc.blocks.every((block) =>
    block.type === 'paragraph' || block.type === 'heading' || block.type === 'quote'
      ? !inlineText(block.content).trim()
      : block.type === 'list'
        ? block.items.every((item) => !inlineText(item).trim())
        : block.type === 'code'
          ? !block.text.trim()
          : false,
  );

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function Inlines({ content }: { content: RichInline[] }) {
  return content.map((part, index) => {
    let node: ReactNode = part.text.split('\n').map((line, lineIndex) => (
      <Fragment key={lineIndex}>
        {lineIndex > 0 && <br />}
        {line}
      </Fragment>
    ));
    if (part.code) node = <code>{node}</code>;
    if (part.underline) node = <u>{node}</u>;
    if (part.italic) node = <em>{node}</em>;
    if (part.bold) node = <strong>{node}</strong>;
    if (part.href && SAFE_HREF.test(part.href)) {
      node = (
        <a href={part.href} target="_blank" rel="noopener noreferrer nofollow">
          {node}
        </a>
      );
    }
    return <Fragment key={index}>{node}</Fragment>;
  });
}

/** An article's body, as the reader sees it. `imageSrc` turns a picture's id into its address. */
export function RichTextView({ doc, imageSrc, className }: { doc: RichDocument; imageSrc(imageId: string): string; className?: string }) {
  return (
    <div className={cn('rich-text', className)}>
      {doc.blocks.map((block, index) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={index}>
                <Inlines content={block.content} />
              </p>
            );
          case 'heading':
            return block.level === 2 ? (
              <h2 key={index}>
                <Inlines content={block.content} />
              </h2>
            ) : (
              <h3 key={index}>
                <Inlines content={block.content} />
              </h3>
            );
          case 'quote':
            return (
              <blockquote key={index}>
                <Inlines content={block.content} />
              </blockquote>
            );
          case 'list': {
            const items = block.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                <Inlines content={item} />
              </li>
            ));
            return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
          }
          case 'code':
            return <pre key={index}>{block.text}</pre>;
          case 'image':
            return (
              <figure key={index}>
                <a href={imageSrc(block.imageId)} target="_blank" rel="noopener noreferrer" title="Open the full-size picture">
                  <img src={imageSrc(block.imageId)} alt={block.alt} loading="lazy" />
                </a>
              </figure>
            );
          case 'divider':
            return <hr key={index} />;
        }
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HTML <-> document, for the editor
// ---------------------------------------------------------------------------

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

function inlinesToHtml(content: RichInline[]) {
  return content
    .map((part) => {
      let html = escapeHtml(part.text).replace(/\n/g, '<br>');
      if (part.code) html = `<code>${html}</code>`;
      if (part.underline) html = `<u>${html}</u>`;
      if (part.italic) html = `<i>${html}</i>`;
      if (part.bold) html = `<b>${html}</b>`;
      if (part.href && SAFE_HREF.test(part.href)) html = `<a href="${escapeHtml(part.href)}">${html}</a>`;
      return html;
    })
    .join('');
}

const figureHtml = (imageId: string, alt: string, src: string) =>
  `<figure contenteditable="false" data-image-id="${escapeHtml(imageId)}" data-alt="${escapeHtml(alt)}"><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"></figure>`;

/** The editor's HTML for a document. Everything in it is generated here, with the text escaped. */
export function documentToHtml(doc: RichDocument, imageSrc: (imageId: string) => string) {
  const html = doc.blocks
    .map((block) => {
      switch (block.type) {
        case 'paragraph':
          return `<p>${inlinesToHtml(block.content) || '<br>'}</p>`;
        case 'heading':
          return `<h${block.level}>${inlinesToHtml(block.content) || '<br>'}</h${block.level}>`;
        case 'quote':
          return `<blockquote>${inlinesToHtml(block.content) || '<br>'}</blockquote>`;
        case 'list': {
          const tag = block.ordered ? 'ol' : 'ul';
          return `<${tag}>${block.items.map((item) => `<li>${inlinesToHtml(item) || '<br>'}</li>`).join('')}</${tag}>`;
        }
        case 'code':
          // Line breaks as <br>, the way the editor writes them.
          return `<pre>${escapeHtml(block.text).replace(/\n/g, '<br>') || '<br>'}</pre>`;
        case 'image':
          return figureHtml(block.imageId, block.alt, imageSrc(block.imageId));
        case 'divider':
          return '<hr>';
      }
    })
    .join('');
  return html || '<p><br></p>';
}

type Marks = Omit<RichInline, 'text'>;

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DETAILS', 'DIV', 'DL', 'DT', 'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2',
  'H3', 'H4', 'H5', 'H6', 'HEADER', 'HR', 'IMG', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'SUMMARY', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH',
  'THEAD', 'TR', 'UL',
]);
/** Never content, wherever it comes from. */
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'CANVAS', 'VIDEO', 'AUDIO', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'HEAD', 'TITLE', 'META', 'LINK']);

const isElement = (node: Node): node is HTMLElement => node.nodeType === Node.ELEMENT_NODE;
const hasBlocks = (element: HTMLElement) => [...element.querySelectorAll('*')].some((child) => BLOCK_TAGS.has(child.tagName));

function marksOf(element: HTMLElement, marks: Marks): Marks {
  const next = { ...marks };
  const tag = element.tagName;
  const weight = element.style.fontWeight;
  if (tag === 'B' || tag === 'STRONG' || weight === 'bold' || Number(weight) >= 600) next.bold = true;
  // Google Docs wraps what it copies in <b style="font-weight:normal">.
  if (weight === 'normal' || (weight && Number(weight) < 600)) delete next.bold;
  if (tag === 'I' || tag === 'EM' || element.style.fontStyle === 'italic') next.italic = true;
  if (tag === 'U' || element.style.textDecoration.includes('underline') || element.style.textDecorationLine.includes('underline')) next.underline = true;
  if (tag === 'CODE' || tag === 'KBD' || tag === 'SAMP' || tag === 'TT') next.code = true;
  if (tag === 'A') {
    const href = element.getAttribute('href')?.trim();
    if (href && SAFE_HREF.test(href)) next.href = href;
  }
  return next;
}

/** The text inside an element. With `lines`, block boundaries inside it become line breaks. */
function collectInlines(node: Node, marks: Marks, out: RichInline[], lines = false) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').replace(/[\s ]+/g, ' ');
    if (text) out.push({ text, ...marks });
    return;
  }
  if (!isElement(node) || SKIPPED_TAGS.has(node.tagName) || node.tagName === 'IMG' || node.tagName === 'FIGURE') return;
  if (node.tagName === 'BR') {
    out.push({ text: '\n', ...marks });
    return;
  }
  const next = marksOf(node, marks);
  const block = lines && BLOCK_TAGS.has(node.tagName);
  if (block && out.length > 0 && !out[out.length - 1].text.endsWith('\n')) out.push({ text: '\n' });
  for (const child of node.childNodes) collectInlines(child, next, out, lines);
  if (block && out.length > 0 && !out[out.length - 1].text.endsWith('\n')) out.push({ text: '\n' });
}

const sameMarks = (a: RichInline, b: RichInline) => a.bold === b.bold && a.italic === b.italic && a.underline === b.underline && a.code === b.code && a.href === b.href;

/** Joins runs with the same formatting, and trims the ends and the spaces around line breaks. */
function tidy(parts: RichInline[]): RichInline[] {
  const merged: RichInline[] = [];
  for (const part of parts) {
    const clean: RichInline = { text: part.text };
    for (const mark of ['bold', 'italic', 'underline', 'code'] as const) if (part[mark]) clean[mark] = true;
    if (part.href) clean.href = part.href;
    const last = merged[merged.length - 1];
    if (last && sameMarks(last, clean)) last.text += clean.text;
    else merged.push(clean);
  }
  for (const part of merged) part.text = part.text.replace(/ *\n */g, '\n').replace(/ {2,}/g, ' ');
  if (merged.length) merged[0].text = merged[0].text.replace(/^[\s]+/, '');
  if (merged.length) merged[merged.length - 1].text = merged[merged.length - 1].text.replace(/[\s]+$/, '');
  return merged.filter((part) => part.text !== '');
}

const inlinesOf = (element: HTMLElement, lines = false) => {
  const out: RichInline[] = [];
  for (const child of element.childNodes) collectInlines(child, marksOf(element, {}), out, lines);
  return tidy(out);
};

function imageBlock(element: HTMLElement): RichBlock | null {
  const holder = element.dataset.imageId ? element : element.querySelector<HTMLElement>('[data-image-id]');
  const imageId = holder?.dataset.imageId;
  if (!holder || !imageId) return null;
  return { type: 'image', imageId, alt: (holder.dataset.alt ?? holder.querySelector('img')?.getAttribute('alt') ?? holder.getAttribute('alt') ?? '').trim().slice(0, 300) };
}

/** A code block's text as written: line breaks (the editor writes them as <br>) and blocks inside it become new lines. */
function preText(node: Node) {
  let text = '';
  const walk = (current: Node) => {
    if (current.nodeType === Node.TEXT_NODE) {
      text += current.textContent ?? '';
    } else if (isElement(current) && !SKIPPED_TAGS.has(current.tagName)) {
      if (current.tagName === 'BR') {
        text += '\n';
        return;
      }
      const block = BLOCK_TAGS.has(current.tagName);
      if (block && text && !text.endsWith('\n')) text += '\n';
      current.childNodes.forEach(walk);
      if (block && text && !text.endsWith('\n')) text += '\n';
    }
  };
  node.childNodes.forEach(walk);
  return text.replace(/ /g, ' ');
}

function listItems(list: HTMLElement): RichInline[][] {
  const items: RichInline[][] = [];
  for (const child of list.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (child.tagName === 'LI') {
      // Nested lists become items of their own.
      const clone = child.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('ul, ol').forEach((nested) => nested.remove());
      const content = inlinesOf(clone, true);
      if (content.length) items.push(content);
      child.querySelectorAll(':scope > ul, :scope > ol').forEach((nested) => items.push(...listItems(nested as HTMLElement)));
    } else if (child.tagName === 'UL' || child.tagName === 'OL') {
      items.push(...listItems(child));
    }
  }
  return items;
}

function walkBlocks(parent: Node, blocks: RichBlock[]) {
  let pending: RichInline[] = [];
  const flush = () => {
    const content = tidy(pending);
    pending = [];
    if (content.length) blocks.push({ type: 'paragraph', content });
  };
  for (const child of parent.childNodes) {
    if (!isElement(child)) {
      collectInlines(child, {}, pending);
      continue;
    }
    const tag = child.tagName;
    if (SKIPPED_TAGS.has(tag)) continue;
    if (!BLOCK_TAGS.has(tag)) {
      if (hasBlocks(child)) {
        flush();
        walkBlocks(child, blocks);
      } else {
        collectInlines(child, {}, pending);
      }
      continue;
    }
    flush();
    if (tag === 'FIGURE' || tag === 'IMG') {
      const image = imageBlock(child);
      if (image) blocks.push(image);
    } else if (tag === 'HR') {
      blocks.push({ type: 'divider' });
    } else if (tag === 'PRE') {
      const text = preText(child).replace(/\n+$/, '');
      if (text.trim()) blocks.push({ type: 'code', text });
    } else if (/^H[1-6]$/.test(tag)) {
      const content = inlinesOf(child).map((part) => ({ ...part, bold: undefined }));
      if (content.length) blocks.push({ type: 'heading', level: tag === 'H1' || tag === 'H2' ? 2 : 3, content: tidy(content) });
    } else if (tag === 'BLOCKQUOTE') {
      const content = inlinesOf(child, true);
      if (content.length) blocks.push({ type: 'quote', content });
    } else if (tag === 'UL' || tag === 'OL') {
      const items = listItems(child);
      if (items.length) blocks.push({ type: 'list', ordered: tag === 'OL', items });
    } else if (hasBlocks(child)) {
      walkBlocks(child, blocks);
    } else {
      const content = inlinesOf(child);
      if (content.length) blocks.push({ type: 'paragraph', content });
    }
  }
  flush();
}

/** The document in an editor's (or pasted) HTML. Anything it doesn't know becomes plain text, or is left out. */
export function htmlToDocument(root: Node): RichDocument {
  const blocks: RichBlock[] = [];
  walkBlocks(root, blocks);
  return { version: 1, blocks };
}

// ---------------------------------------------------------------------------
// Editing
// ---------------------------------------------------------------------------

const emptyParagraph = () => {
  const paragraph = document.createElement('p');
  paragraph.append(document.createElement('br'));
  return paragraph;
};

/** Pictures can't be typed into, so there's always a line to type on before, between, and after them. Empty lines aren't saved. */
function keepTypeable(editor: HTMLElement) {
  editor.querySelectorAll('figure[data-image-id]').forEach((figure) => figure.setAttribute('contenteditable', 'false'));
  editor.querySelectorAll(':scope > figure[data-image-id]').forEach((figure) => {
    const next = figure.nextElementSibling;
    if (!next || next.matches('figure[data-image-id]')) figure.after(emptyParagraph());
    if (!figure.previousElementSibling) figure.before(emptyParagraph());
  });
}

export interface RichTextEditorHandle {
  getDocument(): RichDocument;
  focus(): void;
}

type BlockFormat = 'p' | 'h2' | 'h3' | 'blockquote' | 'pre';

const BLOCK_FORMATS: { value: BlockFormat; label: string; icon: typeof Pilcrow }[] = [
  { value: 'p', label: 'Text', icon: Pilcrow },
  { value: 'h2', label: 'Heading', icon: Heading2 },
  { value: 'h3', label: 'Subheading', icon: Heading3 },
  { value: 'blockquote', label: 'Note', icon: Quote },
  { value: 'pre', label: 'Code block', icon: SquareCode },
];

/** A web address as typed: a bare domain gets https://, and an email address becomes a mailto: link. */
export function normalizeHref(value: string) {
  const href = value.trim();
  if (!href) return '';
  if (SAFE_HREF.test(href)) return href;
  if (/^[^\s@/]+@[^\s@/]+\.[a-z]{2,}$/i.test(href)) return `mailto:${href}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return '';
  return `https://${href.replace(/^\/+/, '')}`;
}

function ToolbarButton({ label, icon: Icon, active, disabled, onClick }: { label: string; icon: typeof Bold; active?: boolean; disabled?: boolean; onClick(): void }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // Keeps the selection in the editor.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40',
        active && 'bg-brand-50 text-brand-800 hover:bg-brand-100',
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

const Divider = () => <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />;

interface LinkDraft {
  href: string;
  text: string;
  /** The link being changed, if the cursor is in one. */
  existing: HTMLAnchorElement | null;
  /** Whether text is selected, so it becomes the link's text. */
  selected: boolean;
}

/**
 * A formatted-text editor: headings, bold, italic, underline, code, lists, notes, links, dividers, and pictures. What
 * it holds is read back as a `RichDocument`, so pasted markup never reaches the article as-is.
 */
export function RichTextEditor({
  ref,
  initial,
  imageSrc,
  uploadImage,
  onChange,
  onError,
  placeholder = 'Start writing…',
  labelledBy,
}: {
  ref?: Ref<RichTextEditorHandle>;
  initial: RichDocument;
  imageSrc(imageId: string): string;
  /** Uploads a picture, giving back its id. */
  uploadImage(file: File): Promise<{ id: string }>;
  onChange?(): void;
  onError?(message: string): void;
  placeholder?: string;
  labelledBy?: string;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  // Pictures that belong in this article, so pasting one moved within it keeps it.
  const knownImages = useRef(new Set(initial.blocks.flatMap((block) => (block.type === 'image' ? [block.imageId] : []))));
  const internalDrag = useRef(false);
  const [formats, setFormats] = useState({ bold: false, italic: false, underline: false, ul: false, ol: false, block: 'p' as string, code: false, link: false });
  const [empty, setEmpty] = useState(() => isDocumentEmpty(initial));
  const [uploading, setUploading] = useState(0);
  const [link, setLink] = useState<LinkDraft | null>(null);
  const [linkError, setLinkError] = useState('');
  const [picture, setPicture] = useState<{ figure: HTMLElement; alt: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    getDocument: () => (editorRef.current ? htmlToDocument(editorRef.current) : EMPTY_DOCUMENT),
    focus: () => editorRef.current?.focus(),
  }));

  // The starting content goes in once; after that the editor owns its HTML.
  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = documentToHtml(initial, imageSrc);
    keepTypeable(editorRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inEditor = (node: Node | null) => !!node && !!editorRef.current?.contains(node);

  const refreshFormats = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !inEditor(selection.anchorNode)) return;
    savedRange.current = selection.getRangeAt(0).cloneRange();
    const anchor = selection.anchorNode instanceof Element ? selection.anchorNode : selection.anchorNode?.parentElement;
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      ul: document.queryCommandState('insertUnorderedList'),
      ol: document.queryCommandState('insertOrderedList'),
      block: (document.queryCommandValue('formatBlock') || 'p').toLowerCase(),
      code: !!anchor?.closest('code'),
      link: !!anchor?.closest('a'),
    });
  };

  useEffect(() => {
    document.addEventListener('selectionchange', refreshFormats);
    return () => document.removeEventListener('selectionchange', refreshFormats);
  });

  const changed = () => {
    const editor = editorRef.current;
    if (!editor) return;
    keepTypeable(editor);
    setEmpty(isDocumentEmpty(htmlToDocument(editor)));
    onChange?.();
  };

  /** Puts the cursor back in the editor where it was, when something else (a dialog, the file picker) took it. */
  const restoreSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) return;
    if (document.activeElement === editor && inEditor(selection.anchorNode)) return;
    editor.focus({ preventScroll: true });
    if (savedRange.current && inEditor(savedRange.current.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    } else if (!inEditor(selection.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const exec = (command: string, value?: string) => {
    restoreSelection();
    document.execCommand('defaultParagraphSeparator', false, 'p');
    document.execCommand(command, false, value);
    refreshFormats();
    changed();
  };

  const placeCaret = (node: Node) => {
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(false);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  };

  /** The code block the cursor is in, if any. */
  const currentPre = () => {
    const node = window.getSelection()?.anchorNode;
    const pre = (node instanceof Element ? node : node?.parentElement)?.closest('pre');
    return pre && inEditor(pre) ? (pre as HTMLElement) : null;
  };

  /** Choosing the block's current format again turns it back into plain text. */
  const setBlock = (format: BlockFormat) => {
    restoreSelection();
    const pre = currentPre();
    if (pre) {
      // The browser would put the paragraph inside the code block, so this one is done by hand; its lines stay lines.
      const paragraph = document.createElement('p');
      paragraph.append(...pre.childNodes);
      if (!paragraph.hasChildNodes()) paragraph.append(document.createElement('br'));
      pre.replaceWith(paragraph);
      placeCaret(paragraph);
      if (format !== 'p' && format !== 'pre') document.execCommand('formatBlock', false, format);
      refreshFormats();
      changed();
      return;
    }
    const current = (document.queryCommandValue('formatBlock') || 'p').toLowerCase();
    exec('formatBlock', current === format && format !== 'p' ? 'p' : format);
  };

  /** Enter on an empty last line of a code block leaves the block for a new paragraph. */
  const leaveCodeBlock = () => {
    const pre = currentPre();
    const selection = window.getSelection();
    if (!pre || !selection?.rangeCount || !selection.isCollapsed) return false;
    const caret = selection.getRangeAt(0);
    const before = document.createRange();
    before.setStart(pre, 0);
    before.setEnd(caret.startContainer, caret.startOffset);
    const after = document.createRange();
    after.setStart(caret.endContainer, caret.endOffset);
    after.setEnd(pre, pre.childNodes.length);
    const typed = preText(before.cloneContents());
    if ((typed !== '' && !typed.endsWith('\n')) || preText(after.cloneContents()).trim()) return false;
    after.deleteContents();
    // The break that started the empty line.
    if (pre.lastChild?.nodeName === 'BR') pre.lastChild.remove();
    const paragraph = emptyParagraph();
    pre.after(paragraph);
    if (!preText(pre).trim()) pre.remove();
    placeCaret(paragraph);
    changed();
    return true;
  };

  const toggleCode = () => {
    restoreSelection();
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const anchor = selection.anchorNode instanceof Element ? selection.anchorNode : selection.anchorNode?.parentElement;
    const code = anchor?.closest('code');
    if (code && inEditor(code)) {
      const range = document.createRange();
      range.selectNode(code);
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('insertText', false, code.textContent ?? '');
    } else if (!selection.isCollapsed) {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selection.toString())}</code>`);
    }
    refreshFormats();
    changed();
  };

  const insertHtml = (html: string) => {
    restoreSelection();
    document.execCommand('defaultParagraphSeparator', false, 'p');
    document.execCommand('insertHTML', false, html);
    changed();
  };

  const addPictures = async (files: File[]) => {
    const pictures = files.filter((file) => file.type.startsWith('image/'));
    if (pictures.length === 0) return;
    // Remember where they go before anything else moves the cursor.
    const selection = window.getSelection();
    if (selection?.rangeCount && inEditor(selection.anchorNode)) savedRange.current = selection.getRangeAt(0).cloneRange();
    setUploading((count) => count + pictures.length);
    for (const file of pictures) {
      try {
        const { id } = await uploadImage(file);
        knownImages.current.add(id);
        const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim().slice(0, 300);
        insertHtml(`${figureHtml(id, alt, imageSrc(id))}<p><br></p>`);
        const after = window.getSelection();
        if (after?.rangeCount) savedRange.current = after.getRangeAt(0).cloneRange();
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'That picture couldn’t be added');
      } finally {
        setUploading((count) => count - 1);
      }
    }
  };

  /** Pasted or dropped content, cleaned to what an article can hold. */
  const insertTransfer = (data: DataTransfer) => {
    const files = [...data.files];
    if (files.some((file) => file.type.startsWith('image/'))) {
      void addPictures(files);
      return;
    }
    const html = data.getData('text/html');
    // Into a code block, only the text.
    if (html && !currentPre()) {
      // A parsed document is inert: nothing in it runs or loads.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const doc = htmlToDocument(parsed.body);
      doc.blocks = doc.blocks.filter((block) => block.type !== 'image' || knownImages.current.has(block.imageId));
      if (doc.blocks.length > 0) {
        // A single paragraph goes in as text, rather than breaking the paragraph it lands in.
        const only = doc.blocks[0];
        insertHtml(doc.blocks.length === 1 && only.type === 'paragraph' ? inlinesToHtml(only.content) : documentToHtml(doc, imageSrc));
      }
      return;
    }
    const text = data.getData('text/plain');
    if (text) {
      restoreSelection();
      if (currentPre()) {
        // Line by line, so the lines stay in the code block.
        text.replace(/\r\n?/g, '\n').split('\n').forEach((line, index) => {
          if (index > 0) document.execCommand('insertLineBreak');
          if (line) document.execCommand('insertText', false, line);
        });
      } else {
        document.execCommand('insertText', false, text);
      }
      changed();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    insertTransfer(event.clipboardData);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (internalDrag.current) return;
    event.preventDefault();
    const caret = document.caretRangeFromPoint?.(event.clientX, event.clientY);
    if (caret) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(caret);
      savedRange.current = caret.cloneRange();
    }
    insertTransfer(event.dataTransfer);
  };

  const openLink = () => {
    restoreSelection();
    const selection = window.getSelection();
    const anchor = selection?.anchorNode instanceof Element ? selection.anchorNode : selection?.anchorNode?.parentElement;
    const existing = (anchor?.closest('a') as HTMLAnchorElement | null) ?? null;
    if (selection?.rangeCount) savedRange.current = selection.getRangeAt(0).cloneRange();
    setLinkError('');
    setLink({
      href: existing?.getAttribute('href') ?? '',
      text: existing?.textContent ?? selection?.toString() ?? '',
      existing: existing && inEditor(existing) ? existing : null,
      selected: !!selection && !selection.isCollapsed,
    });
  };

  // The dialogs' forms stop their submit here: React passes events up through portals, to any form around the editor.
  const saveLink = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!link) return;
    const href = normalizeHref(link.href);
    if (!href) {
      setLinkError('Enter a web address, like https://example.org, or an email address');
      return;
    }
    if (link.existing) {
      link.existing.setAttribute('href', href);
      if (link.text.trim() && link.text !== link.existing.textContent) link.existing.textContent = link.text.trim();
      changed();
    } else if (link.selected) {
      exec('createLink', href);
    } else {
      insertHtml(`<a href="${escapeHtml(href)}">${escapeHtml(link.text.trim() || href.replace(/^mailto:/i, ''))}</a>&nbsp;`);
    }
    setLink(null);
  };

  const removeLink = () => {
    const existing = link?.existing;
    if (existing) {
      const range = document.createRange();
      range.selectNodeContents(existing);
      savedRange.current = range;
      exec('unlink');
    }
    setLink(null);
  };

  const onEditorClick = (target: EventTarget) => {
    const figure = target instanceof Element ? (target.closest('figure[data-image-id]') as HTMLElement | null) : null;
    if (figure && inEditor(figure)) setPicture({ figure, alt: figure.dataset.alt ?? '' });
  };

  const savePicture = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!picture) return;
    const alt = picture.alt.trim().slice(0, 300);
    picture.figure.dataset.alt = alt;
    picture.figure.querySelector('img')?.setAttribute('alt', alt);
    setPicture(null);
    changed();
  };

  const removePicture = () => {
    picture?.figure.remove();
    setPicture(null);
    changed();
  };

  const blockLabel = BLOCK_FORMATS.find((format) => format.value === formats.block)?.label ?? 'Text';

  return (
    <div className="rounded-lg border border-slate-300 bg-white shadow-sm focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
      <div role="toolbar" aria-label="Formatting" className="sticky top-14 z-10 flex flex-wrap items-center gap-0.5 rounded-t-lg border-b lg:top-0 border-slate-200 bg-slate-50/95 px-2 py-1.5 backdrop-blur">
        {BLOCK_FORMATS.map((format) => (
          <ToolbarButton key={format.value} label={format.label} icon={format.icon} active={formats.block === format.value} onClick={() => setBlock(format.value)} />
        ))}
        <Divider />
        <ToolbarButton label="Bold (Ctrl+B)" icon={Bold} active={formats.bold} onClick={() => exec('bold')} />
        <ToolbarButton label="Italic (Ctrl+I)" icon={Italic} active={formats.italic} onClick={() => exec('italic')} />
        <ToolbarButton label="Underline (Ctrl+U)" icon={Underline} active={formats.underline} onClick={() => exec('underline')} />
        <ToolbarButton label="Code" icon={Code} active={formats.code} onClick={toggleCode} />
        <ToolbarButton label="Link (Ctrl+K)" icon={Link2} active={formats.link} onClick={openLink} />
        <ToolbarButton label="Clear formatting" icon={RemoveFormatting} onClick={() => exec('removeFormat')} />
        <Divider />
        <ToolbarButton label="Bulleted list" icon={List} active={formats.ul} onClick={() => exec('insertUnorderedList')} />
        <ToolbarButton label="Numbered list" icon={ListOrdered} active={formats.ol} onClick={() => exec('insertOrderedList')} />
        <ToolbarButton label="Divider" icon={Minus} onClick={() => exec('insertHorizontalRule')} />
        <ToolbarButton
          label="Add a picture"
          icon={ImagePlus}
          onClick={() => {
            const selection = window.getSelection();
            if (selection?.rangeCount && inEditor(selection.anchorNode)) savedRange.current = selection.getRangeAt(0).cloneRange();
            fileInput.current?.click();
          }}
        />
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            void addPictures(files);
          }}
        />
        <Divider />
        <ToolbarButton label="Undo (Ctrl+Z)" icon={Undo2} onClick={() => exec('undo')} />
        <ToolbarButton label="Redo (Ctrl+Y)" icon={Redo2} onClick={() => exec('redo')} />
        <span className="ml-auto flex items-center gap-2 pr-1 text-xs text-slate-500">
          {uploading > 0 ? (
            <>
              <Spinner className="size-3.5" /> Adding {uploading === 1 ? 'a picture' : `${uploading} pictures`}…
            </>
          ) : (
            <span className="hidden sm:inline">{blockLabel}</span>
          )}
        </span>
      </div>

      <div className="relative">
        {empty && <p className="pointer-events-none absolute top-4 left-4 text-[15px] text-slate-400">{placeholder}</p>}
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline
          aria-labelledby={labelledBy}
          contentEditable
          suppressContentEditableWarning
          spellCheck
          className="rich-text rich-text-editor min-h-[24rem] px-4 py-4 outline-none"
          onInput={changed}
          onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
          onPaste={onPaste}
          onDrop={onDrop}
          onDragStart={() => (internalDrag.current = true)}
          onDragEnd={() => (internalDrag.current = false)}
          onClick={(event) => onEditorClick(event.target)}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
              event.preventDefault();
              openLink();
            } else if (event.key === 'Enter' && !event.shiftKey && leaveCodeBlock()) {
              event.preventDefault();
            }
          }}
        />
      </div>

      <Modal
        open={!!link}
        onClose={() => setLink(null)}
        size="sm"
        title={link?.existing ? 'Edit link' : 'Add a link'}
        footer={
          <>
            {link?.existing && (
              <Button variant="ghost" className="mr-auto" onClick={removeLink}>
                Remove link
              </Button>
            )}
            <Button onClick={() => setLink(null)}>Cancel</Button>
            <Button variant="primary" type="submit" form="rich-text-link">
              {link?.existing ? 'Save link' : 'Add link'}
            </Button>
          </>
        }
      >
        {link && (
          <form id="rich-text-link" onSubmit={saveLink} className="space-y-4">
            <Field label="Web address or email" error={linkError || undefined}>
              <Input autoFocus value={link.href} onChange={(event) => setLink({ ...link, href: event.target.value })} placeholder="https://example.org/help" maxLength={2000} />
            </Field>
            {!link.selected && (
              <Field label="Text to show" hint={link.existing ? undefined : 'Leave it empty to show the address'}>
                <Input value={link.text} onChange={(event) => setLink({ ...link, text: event.target.value })} maxLength={500} />
              </Field>
            )}
          </form>
        )}
      </Modal>

      <Modal
        open={!!picture}
        onClose={() => setPicture(null)}
        size="sm"
        title="Picture"
        footer={
          <>
            <Button variant="ghost" className="mr-auto text-red-700" onClick={removePicture}>
              Remove picture
            </Button>
            <Button onClick={() => setPicture(null)}>Cancel</Button>
            <Button variant="primary" type="submit" form="rich-text-picture">
              Save
            </Button>
          </>
        }
      >
        {picture && (
          <form id="rich-text-picture" onSubmit={savePicture} className="space-y-4">
            <img src={picture.figure.querySelector('img')?.getAttribute('src') ?? ''} alt="" className="max-h-48 rounded-lg border border-slate-200" />
            <Field label="Description" hint="Read aloud by screen readers, and shown if the picture can’t load.">
              <Input autoFocus value={picture.alt} onChange={(event) => setPicture({ ...picture, alt: event.target.value })} maxLength={300} />
            </Field>
          </form>
        )}
      </Modal>
    </div>
  );
}
